import {useEffect, useRef, useState} from "react";
import {useLocation} from "react-router";
import {twJoin} from "tailwind-merge";
import {CtaButtonFilled} from "~/components/ctaButtonFilled";
import {useFrontendProxyUrl} from "~/root/rootLoaderData";

/* Routes whose hero is a dark-background section.
 * When the header sits over one of these AND the page hasn't been scrolled,
 * we flip the header to white-on-transparent so the text stays legible. */
const ROUTES_WITH_DARK_HERO = new Set(["/apps"]);

function BrandMark({onDarkBg, symbolUrl}: {onDarkBg: boolean; symbolUrl: string}) {
    return (
        <div className="flex items-center gap-2 no-underline">
            <div className="max-xl:h-7 max-xl:w-7 xl:h-9 xl:w-9 overflow-hidden grid place-items-center shrink-0">
                <img
                    alt="Intellsys OS"
                    src={symbolUrl}
                    className="w-[155%] h-[155%] object-contain"
                />
            </div>
            <span
                className={twJoin(
                    "font-semibold tracking-tight max-xl:text-[1rem] xl:text-[1.25rem]",
                    onDarkBg ? "text-[#FFFFFF]" : "text-text-primary",
                )}
            >
                Intellsys
            </span>
        </div>
    );
}

/**
 * Direct port of growthjockey.com v2's HeaderDropdownMenu pattern.
 *  - Click button to toggle isOpen
 *  - Click outside or scroll to close
 *  - Active state when current pathname matches `path`
 *  - Underline-on-hover via 2px border-b that picks up current text color
 */
function HeaderDropdownMenu({
    text,
    path,
    children,
    onDarkBg,
}: {
    text: string;
    path?: string;
    children?: React.ReactNode;
    onDarkBg: boolean;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const location = useLocation();

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (menuRef.current && !menuRef.current.contains(target)) {
                setIsOpen(false);
            }
        };
        const handleScroll = () => {
            setIsOpen(false);
        };
        const timer = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
            window.addEventListener("scroll", handleScroll);
        }, 100);
        return () => {
            clearTimeout(timer);
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleScroll);
        };
    }, [isOpen]);

    const isActive = isOpen || (path != null && location.pathname === path);

    return (
        <div
            ref={menuRef}
            className={twJoin("relative", onDarkBg ? "hover:text-primary-200" : "hover:text-primary-800")}
        >
            <button
                type="button"
                className={twJoin(
                    "w-[9.38rem] text-nav-text-b px-[1.25rem] py-sm grid grid-cols-1 place-items-center border-t-[0.125rem] border-b-[0.125rem] border-t-transparent transition-colors duration-[300ms]",
                    isActive
                        ? onDarkBg
                            ? "border-b-[0.125rem] text-[#FFFFFF]"
                            : "border-b-[0.125rem] text-primary-500"
                        : "border-b-transparent hover:border-b-current",
                )}
                onClick={() => setIsOpen((prev) => !prev)}
            >
                {text}
            </button>
            {isOpen && children}
        </div>
    );
}

function NavLinkButton({href, text, onDarkBg}: {href: string; text: string; onDarkBg: boolean}) {
    return (
        <a
            href={href}
            className={twJoin("no-underline", onDarkBg ? "text-[#FFFFFF]" : "text-text-primary")}
        >
            {/* Intentionally NOT passing `path`, we don't want a persistent active underline on the current route. */}
            <HeaderDropdownMenu
                text={text}
                onDarkBg={onDarkBg}
            />
        </a>
    );
}

function MobileHeader({
    continueToIntellsysUrl,
    isScrolled,
    onDarkBg,
    symbolUrl,
}: {
    continueToIntellsysUrl: string;
    isScrolled: boolean;
    onDarkBg: boolean;
    symbolUrl: string;
}) {
    const [isOpen, setIsOpen] = useState(false);

    // Lock background scroll while the menu is open so the panel feels modal.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [isOpen]);

    // Once the menu is open the header gets a solid background regardless of
    // scroll position, so the toggle button stays readable over any hero.
    const headerHasSolidBg = isScrolled || isOpen;
    const menuOnDarkBg = onDarkBg && !isOpen;

    return (
        <>
            <header
                className={twJoin(
                    "my-auto w-full h-[var(--gj-header-height)] px-6 py-2 grid xl:hidden grid-cols-[minmax(0,1fr)_auto] items-center justify-between z-100 transition-[background,box-shadow] duration-300 relative",
                    menuOnDarkBg ? "text-[#FFFFFF]" : "text-text-primary",
                    headerHasSolidBg ? "bg-surface-secondary-background/95 backdrop-blur-md shadow-sm" : "bg-transparent",
                )}
            >
                <a
                    href="/"
                    className="w-fit no-underline"
                    onClick={() => setIsOpen(false)}
                >
                    <BrandMark
                        onDarkBg={menuOnDarkBg}
                        symbolUrl={symbolUrl}
                    />
                </a>
                <button
                    type="button"
                    className={twJoin(
                        "p-2 transition-colors",
                        menuOnDarkBg ? "text-[#FFFFFF] hover:text-primary-200" : "text-text-primary hover:text-primary-500",
                    )}
                    aria-label={isOpen ? "Close menu" : "Open menu"}
                    aria-expanded={isOpen}
                    onClick={() => setIsOpen((prev) => !prev)}
                >
                    <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                        {isOpen ? (
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 6l12 12M6 18L18 6"
                            />
                        ) : (
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                        )}
                    </svg>
                </button>
            </header>

            {isOpen ? (
                <div className="xl:hidden fixed inset-x-0 top-[var(--gj-header-height)] bottom-0 z-100 bg-surface-secondary-background/98 backdrop-blur-md grid grid-cols-1 content-start gap-4 px-6 pt-lg pb-md3 overflow-y-auto">
                    <a
                        href="/apps"
                        onClick={() => setIsOpen(false)}
                        className="text-main-title text-text-primary no-underline py-3 border-b border-surface-lines-and-borders"
                    >
                        Apps
                    </a>
                    <a
                        href="/contact-us"
                        onClick={() => setIsOpen(false)}
                        className="text-main-title text-text-primary no-underline py-3 border-b border-surface-lines-and-borders"
                    >
                        Contact us
                    </a>
                    <div className="pt-md">
                        <CtaButtonFilled
                            href={continueToIntellsysUrl}
                            text="Continue to Intellsys"
                            arrow
                            onClick={() => setIsOpen(false)}
                        />
                    </div>
                </div>
            ) : null}
        </>
    );
}

function DesktopHeader({
    continueToIntellsysUrl,
    isScrolled,
    onDarkBg,
    symbolUrl,
}: {
    continueToIntellsysUrl: string;
    isScrolled: boolean;
    onDarkBg: boolean;
    symbolUrl: string;
}) {
    return (
        <header
            className={twJoin(
                "relative w-full py-3 px-lg hidden xl:grid grid-cols-[auto_minmax(0,1fr)_auto] gap-[1.125rem] items-center justify-between inset-0 z-100 transition-[background,box-shadow] duration-300",
                onDarkBg ? "text-[#FFFFFF]" : "text-text-primary",
                isScrolled ? "bg-surface-secondary-background/95 backdrop-blur-md shadow-sm" : "bg-transparent",
            )}
        >
            <a
                href="/"
                className="w-fit no-underline"
            >
                <BrandMark
                    onDarkBg={onDarkBg}
                    symbolUrl={symbolUrl}
                />
            </a>

            <div className={twJoin("w-full h-fit grid grid-flow-col auto-cols-auto items-center justify-between", onDarkBg ? "text-[#FFFFFF]" : "text-text-primary")}>
                <div className="grid grid-flow-col auto-cols-auto text-nav-text">
                    <NavLinkButton
                        href="/apps"
                        text="Apps"
                        onDarkBg={onDarkBg}
                    />
                    <NavLinkButton
                        href="/contact-us"
                        text="Contact us"
                        onDarkBg={onDarkBg}
                    />
                </div>

                <div className="grid grid-flow-col auto-cols-auto items-center gap-2 text-nav-text">
                    <CtaButtonFilled
                        href={continueToIntellsysUrl}
                        text="Continue to Intellsys"
                        arrow
                    />
                </div>
            </div>

            <div className="grid grid-flow-col items-center auto-cols-auto gap-[1.125rem]">
                {/* Theme toggle slot, hidden in light-only mode */}
            </div>
        </header>
    );
}

export function Header() {
    const [isScrolled, setIsScrolled] = useState(false);
    const location = useLocation();
    const continueToIntellsysUrl = useFrontendProxyUrl("/u");
    const symbolUrl = useFrontendProxyUrl("/neptune_assets/static/loaderAnimation/intellsysCircle.svg");
    /* White-on-dark only when over a dark-hero page AND user hasn't scrolled past it.
     * Once the user scrolls, the header gains its solid bg + shadow and reverts to
     * normal dark-on-light styling. */
    const onDarkBg = ROUTES_WITH_DARK_HERO.has(location.pathname) && !isScrolled;

    useEffect(() => {
        const handler = () => setIsScrolled(window.scrollY > 8);
        handler();
        window.addEventListener("scroll", handler, {passive: true});
        return () => window.removeEventListener("scroll", handler);
    }, []);

    return (
        <div className="w-full fixed top-0 inset-x-0 z-50">
            <MobileHeader
                continueToIntellsysUrl={continueToIntellsysUrl}
                isScrolled={isScrolled}
                onDarkBg={onDarkBg}
                symbolUrl={symbolUrl}
            />
            <DesktopHeader
                continueToIntellsysUrl={continueToIntellsysUrl}
                isScrolled={isScrolled}
                onDarkBg={onDarkBg}
                symbolUrl={symbolUrl}
            />
        </div>
    );
}
